<template>
  <v-form ref="clientForm" v-model="isFormValid" lazy-validation>
    <v-row class="mt-3">
      <v-col cols="12" sm="6">
        <v-btn
          block
          min-height="200px"
          :outlined="newClientOutlined"
          color="primary"
          style="font-size: 30px"
          @click="newClientClickedHandler"
        >
          New<br>Client
        </v-btn>
      </v-col>
      <v-col cols="12" sm="6">
        <v-btn
          block
          min-height="200px"
          :outlined="existingClientOutlined"
          color="primary"
          style="font-size: 30px"
          @click="existingClientClickedHandler"
        >
          Existing<br>Client
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" lg="3" v-if="isExistingClient">
        <ClientsDropDownList
          :is-required="isExistingClient"
          :id="existingClientId"
          :readonly="readonly"
          @clientIdChange="clientIdChangeHandler">
        </ClientsDropDownList>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="3" xl="2">
        <v-text-field
          label="Name"
          v-model="name"
          :rules="[Required('Client Name')]"
          :readonly="isExistingClient || readonly"
          :disabled="!clientType"
          validate-on-blur
          outlined
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3" xl="2">
        <InvoiceFrequenciesDropDownList
          :id="invoiceFrequencyId"
          :isRequired="!(this.dealCreationTypeId == CREATE_FROM_ZOHO && (this.statusId == null || this.statusId == this.dealStatuses.InProcess))"
          :readOnly="isExistingClient || readonly"
          :disabled="!clientType"
          :clearable="!readonly && !isExistingClient"
          @change="(newValue) => this.invoiceFrequencyId = newValue"
        ></InvoiceFrequenciesDropDownList>
      </v-col>
      <v-col cols="12" sm="6" md="3" lg="3" xl="2">
        <ThirdPartyPortalsDropDownList
          :id="thirdPartyPortalId"
          :isRequired="false"
          :disabled="!clientType"
          :readonly="isExistingClient || readonly"
          :clearable="!readonly && !isExistingClient"
          @ThirdPartyPortalIdChange="(newValue) => this.thirdPartyPortalId = newValue"
        ></ThirdPartyPortalsDropDownList>
      </v-col>
      <v-col cols="12" sm="6" md="3" xl="2">
        <FormattedNumberField
          label="VMS Fee"
          :value="vmsFee"
          append-icon="percent"
          :disabled="!clientType"
          :clearable="!readonly"
          :readonly="isExistingClient || readonly"
          @valueChange="(newValue) => this.vmsFee = newValue"
        ></FormattedNumberField>
      </v-col>
    </v-row>
    <ButtonsSubmitForm
      class="mt-5"
      :acceptLabel="nextButtonLabel"
      cancelLabel="back"
      :isFormValid="true"
      @save="nextStep"
      @cancel="goBack"
    >
    </ButtonsSubmitForm>
    <MessageBox
      ref="messageBox"
      :message="message"
      color="error"
      closeTextColor="white"
    ></MessageBox>
  </v-form>
</template>

<script>
import { ButtonsSubmitForm } from '@/components/buttons'
import { InvoiceFrequenciesDropDownList } from '@/components/invoiceFrequencies'
import Required from '@/lib/formValidations/required'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { ClientsDropDownList, ThirdPartyPortalsDropDownList } from '@/components/clients'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { NEW_CLIENT, EXISTING_CLIENT } from '@/lib/constants/clientTypes'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MessageBox } from '@/components/shared/messages'
import { dealStatuses } from '@/lib/enum/dealStatuses'

export default {
  components: {
    ButtonsSubmitForm,
    ClientsDropDownList,
    InvoiceFrequenciesDropDownList,
    ThirdPartyPortalsDropDownList,
    FormattedNumberField,
    MessageBox
  },
  props: [
    'readonly'
  ],
  data () {
    return {
      NEW_CLIENT,
      EXISTING_CLIENT,
      newClientOutlined: true,
      existingClientOutlined: true,
      isFormValid: null,
      CREATE_FROM_ZOHO,
      message: null,
      dealStatuses
    }
  },
  computed: {
    ...mapFields('deal', ['id', 'statusId', 'dealCreationTypeId', 'dealClientId']),
    ...mapFields('dealClient',
      [
        'dealId',
        'clientType',
        'existingClientId',
        'name',
        'invoiceFrequencyId',
        'thirdPartyPortalId',
        'vmsFee',
        'contractorExpenses',
        'directPlacementsExpenses'
      ]),
    isExistingClient () {
      return this.clientType === this.EXISTING_CLIENT
    },
    nextButtonLabel () {
      return (!this.statusId && this.dealCreationTypeId === CREATE_FROM_ZOHO) || this.readonly ? 'next' : 'save'
    }
  },
  watch: {
    clientType () {
      if (this.clientType === this.NEW_CLIENT) {
        this.newClientOutlined = false
        this.existingClientOutlined = true
      }
      if (this.clientType === this.EXISTING_CLIENT) {
        this.newClientOutlined = true
        this.existingClientOutlined = false
      }
    }
  },
  methods: {
    ...mapActions('client', ['getClient']),
    ...mapActions('dealClient', ['saveDealClient']),
    Required,
    newClientClickedHandler () {
      if (!this.readonly) {
        this.clientType = this.NEW_CLIENT
        this.clearClient()
      }
      this.$refs.clientForm.resetValidation()
    },
    existingClientClickedHandler () {
      if (!this.readonly) {
        this.clientType = this.EXISTING_CLIENT
        this.clearClient()
      }
      this.$refs.clientForm.resetValidation()
    },
    nextStep () {
      if (!this.readonly && !!this.statusId) {
        if (!this.clientType) {
          this.message = 'Please choose an existing client or create a new one'
          this.$refs.messageBox.show()
        } else if (this.clientType === NEW_CLIENT && !this.name) {
          this.message = 'There are required fields missing'
          this.$refs.clientForm.validate()
          this.$refs.messageBox.show()
        } else if (this.clientType === EXISTING_CLIENT && !this.existingClientId) {
          this.message = 'Please choose an existing client to continue'
          this.$refs.clientForm.validate()
          this.$refs.messageBox.show()
        } else if (this.$refs.clientForm.validate()) {
          this.dealId = this.id
          this.saveDealClient().then((response) => {
            this.dealClientId = response.data.id
            this.$emit('complete')
          })
        }
      } else {
        this.$emit('complete')
      }
    },
    goBack () {
      this.$emit('goBack')
    },
    clientIdChangeHandler (newClientId) {
      this.$refs.clientForm.resetValidation()
      this.existingClientId = newClientId
      this.getClient({ id: this.existingClientId }).then(clientData => {
        this.name = clientData.name
        this.invoiceFrequencyId = clientData.invoiceFrequencyId
        this.thirdPartyPortalId = clientData.thirdPartyPortalId
        this.vmsFee = clientData.vmsFee
        this.contractorExpenses = clientData.contractorExpenses.filter(expense => expense.active === true) || []
        this.directPlacementsExpenses = clientData.directPlacementsExpenses.filter(expense => expense.active === true) || []
      })
    },
    clearClient () {
      this.existingClientId = null
      this.name = null
      this.invoiceFrequencyId = null
      this.thirdPartyPortalId = null
      this.contractorExpenses = []
      this.directPlacementsExpenses = []
      this.vmsFee = null
    }
  }
}
</script>
